import { apiClient } from '@/lib/api'
import { useTelegramStore } from '@/stores/telegram'
import { useAuthStore } from '@/stores/auth'
import { 
  validateTelegramUserForCreation,
  isTelegramBot 
} from '@/utils/telegram-user-mapper'
import type { UnifiedTelegramUser } from '@/stores/telegram'
import type { User } from '@/types/api'

export interface TelegramAuthResult {
  success: boolean
  user?: User
  isNewUser?: boolean
  error?: string
}

export class TelegramAuthService {
  private static instance: TelegramAuthService

  static getInstance(): TelegramAuthService {
    if (!TelegramAuthService.instance) {
      TelegramAuthService.instance = new TelegramAuthService()
    }
    return TelegramAuthService.instance
  }

  async authenticateWithTelegram(
    telegramUser: UnifiedTelegramUser,
    options: {
      autoCreateUser?: boolean
      defaultRole?: 'student' | 'teacher' | 'admin'
    } = {}
  ): Promise<TelegramAuthResult> {
    try {
      console.log('TelegramAuthService: Starting authentication for user:', telegramUser)

      // 總是執行 find or create user 邏輯
      return await this.findOrCreateAndAuthenticate(telegramUser, {
        defaultRole: options.defaultRole || 'student',
        autoCreate: options.autoCreateUser !== false // 預設為 true
      })

    } catch (error) {
      console.error('TelegramAuthService: Authentication failed:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Authentication failed'
      }
    }
  }

  private async findOrCreateAndAuthenticate(
    telegramUser: UnifiedTelegramUser,
    _options: {
      defaultRole: 'student' | 'teacher' | 'admin'
      autoCreate: boolean
    }
  ): Promise<TelegramAuthResult> {
    try {
      console.log('TelegramAuthService: Starting Telegram authentication')

      // 檢查是否為機器人
      if (isTelegramBot(telegramUser)) {
        return {
          success: false,
          error: 'Cannot authenticate Telegram bots'
        }
      }

      // 驗證 Telegram 用戶資料
      const validation = validateTelegramUserForCreation(telegramUser)
      if (!validation.isValid) {
        return {
          success: false,
          error: `Invalid Telegram user data: missing ${validation.missingFields.join(', ')}`
        }
      }

      // 直接調用 Telegram 認證 API
      // 後端會處理：Find or Create User + 生成 JWT Token
      console.log('TelegramAuthService: Calling backend authentication')
      const initData = this.buildTelegramInitData(telegramUser)
      
      const loginResult = await apiClient.telegramLogin(initData)
      console.log('TelegramAuthService: Authentication successful:', loginResult)

      // 存儲認證資訊
      if (typeof window !== 'undefined') {
        localStorage.setItem('access_token', loginResult.token)
        
        if (loginResult.expiresAt) {
          localStorage.setItem('token_expires_at', loginResult.expiresAt)
        }
      }

      // 更新認證狀態
      const authStore = useAuthStore.getState()
      authStore.setAuth(loginResult.token, loginResult.user)

      // 判斷是否為新用戶（通過檢查用戶創建時間）
      const isNewUser = this.isRecentlyCreatedUser(loginResult.user)

      return {
        success: true,
        user: loginResult.user,
        isNewUser
      }

    } catch (error) {
      console.error('TelegramAuthService: Authentication failed:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Authentication failed'
      }
    }
  }

  /**
   * 判斷用戶是否為最近創建的新用戶
   */
  private isRecentlyCreatedUser(user: any): boolean {
    if (!user.createdAt) return false
    
    const createdAt = new Date(user.createdAt)
    const now = new Date()
    const timeDiff = now.getTime() - createdAt.getTime()
    
    // 如果創建時間在 1 分鐘內，認為是新用戶
    return timeDiff < 60 * 1000
  }


  private buildTelegramInitData(telegramUser: UnifiedTelegramUser): string {
    // 根據來源構建不同的 initData 格式
    if (telegramUser.source === 'widget') {
      // Widget 格式：使用 auth_date 和 hash
      const params = new URLSearchParams()
      params.append('id', telegramUser.id.toString())
      params.append('first_name', telegramUser.first_name)
      
      if (telegramUser.last_name) {
        params.append('last_name', telegramUser.last_name)
      }
      if (telegramUser.username) {
        params.append('username', telegramUser.username)
      }
      if (telegramUser.photo_url) {
        params.append('photo_url', telegramUser.photo_url)
      }
      if (telegramUser.auth_date) {
        params.append('auth_date', telegramUser.auth_date.toString())
      }
      if (telegramUser.hash) {
        params.append('hash', telegramUser.hash)
      }

      return params.toString()
    } else {
      // Mini App 格式：優先使用 Telegram 簽名過的原始 initData 字串，
      // 後端會用 bot token 驗證其 HMAC 簽名
      const storeInitData = useTelegramStore.getState().initData
      if (storeInitData) {
        return storeInitData
      }

      if (telegramUser.raw_data && typeof telegramUser.raw_data === 'string') {
        return telegramUser.raw_data
      }

      // 最後備援：自建帶 mock hash 的 initData。
      // 後端僅在非 production 環境接受 mock hash，正式環境會被拒絕
      const userObject = {
        id: telegramUser.id,
        first_name: telegramUser.first_name,
        ...(telegramUser.last_name && { last_name: telegramUser.last_name }),
        ...(telegramUser.username && { username: telegramUser.username }),
        ...(telegramUser.photo_url && { photo_url: telegramUser.photo_url }),
        ...(telegramUser.language_code && { language_code: telegramUser.language_code }),
        ...(telegramUser.is_premium !== undefined && { is_premium: telegramUser.is_premium })
      }

      return `user=${encodeURIComponent(JSON.stringify(userObject))}&auth_date=${Math.floor(Date.now() / 1000)}&hash=mock_hash_for_development`
    }
  }

  async logout(): Promise<void> {
    try {
      // 調用後端登出
      await apiClient.logout()
    } catch (error) {
      console.warn('Backend logout failed:', error)
    } finally {
      // 清理本地存儲
      if (typeof window !== 'undefined') {
        localStorage.removeItem('access_token')
        localStorage.removeItem('token_expires_at')
      }

      // 清理狀態
      const authStore = useAuthStore.getState()
      const telegramStore = useTelegramStore.getState()
      
      authStore.logout()
      telegramStore.clearUser()
    }
  }
}

// 導出單例實例
export const telegramAuthService = TelegramAuthService.getInstance()